import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { TrendingUp, DollarSign, Target, AlertTriangle, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#2563EB', '#F97316', '#10B981', '#8B5CF6', '#EC4899'];

export default function DashboardTab({ projectId }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders, API } = useAuth();

  useEffect(() => {
    fetchInsights();
  }, [projectId]);

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/insights/${projectId}`, getAuthHeaders());
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to fetch insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const companies = insights.filter(i => i.type === 'company');
  const funding = insights.filter(i => i.type === 'funding');
  const metrics = insights.filter(i => i.type === 'metric');
  const decisions = insights.filter(i => i.type === 'decision');
  const trends = insights.filter(i => i.type === 'trend');

  // Aggregate data for charts
  const insightTypeData = [
    { name: 'Companies', value: companies.length, color: '#2563EB' },
    { name: 'Funding', value: funding.length, color: '#10B981' },
    { name: 'Metrics', value: metrics.length, color: '#F97316' },
    { name: 'Decisions', value: decisions.length, color: '#8B5CF6' },
    { name: 'Trends', value: trends.length, color: '#EC4899' },
  ];

  const companyData = companies.slice(0, 8).map(c => ({
    name: c.data.value?.split(' ')[0] || 'Unknown',
    mentions: 1
  }));

  if (insights.length === 0) {
    return (
      <div className="card text-center py-20">
        <BarChart3 className="mx-auto text-slate-300 mb-4" size={64} />
        <h3 className="text-xl font-medium text-slate-900 mb-2">No insights yet</h3>
        <p className="text-slate-600">Add conversations to generate AI-powered insights</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Dashboard</h2>
        <p className="text-sm text-slate-600">AI-extracted insights from your research</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        {/* Stat Cards */}
        <div className="col-span-6 md:col-span-3 card p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="text-blue-600" size={24} />
            <span className="text-xs font-mono text-slate-500 uppercase">Companies</span>
          </div>
          <div className="text-3xl font-heading font-bold text-slate-900">{companies.length}</div>
        </div>

        <div className="col-span-6 md:col-span-3 card p-6">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="text-green-600" size={24} />
            <span className="text-xs font-mono text-slate-500 uppercase">Funding</span>
          </div>
          <div className="text-3xl font-heading font-bold text-slate-900">{funding.length}</div>
        </div>

        <div className="col-span-6 md:col-span-3 card p-6">
          <div className="flex items-center justify-between mb-2">
            <Target className="text-orange-600" size={24} />
            <span className="text-xs font-mono text-slate-500 uppercase">Metrics</span>
          </div>
          <div className="text-3xl font-heading font-bold text-slate-900">{metrics.length}</div>
        </div>

        <div className="col-span-6 md:col-span-3 card p-6">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="text-purple-600" size={24} />
            <span className="text-xs font-mono text-slate-500 uppercase">Decisions</span>
          </div>
          <div className="text-3xl font-heading font-bold text-slate-900">{decisions.length}</div>
        </div>

        {/* Charts */}
        <div className="col-span-12 md:col-span-7 card p-6">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Insight Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={insightTypeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Bar dataKey="value" fill="#2563EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="col-span-12 md:col-span-5 card p-6">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Insight Types</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={insightTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => entry.name}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {insightTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insight Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Companies */}
        {companies.length > 0 && (
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-blue-600" size={20} />
              Companies Mentioned
            </h3>
            <div className="space-y-2">
              {companies.slice(0, 10).map((insight) => (
                <div key={insight.id} className="bg-slate-50 rounded-md px-4 py-3 text-sm text-slate-700">
                  {insight.data.value}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Funding */}
        {funding.length > 0 && (
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <DollarSign className="text-green-600" size={20} />
              Funding Information
            </h3>
            <div className="space-y-2">
              {funding.slice(0, 10).map((insight) => (
                <div key={insight.id} className="bg-slate-50 rounded-md px-4 py-3 text-sm text-slate-700">
                  {insight.data.value}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metrics */}
        {metrics.length > 0 && (
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Target className="text-orange-600" size={20} />
              Key Metrics
            </h3>
            <div className="space-y-2">
              {metrics.slice(0, 10).map((insight) => (
                <div key={insight.id} className="bg-slate-50 rounded-md px-4 py-3 text-sm text-slate-700">
                  {insight.data.value}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Decisions */}
        {decisions.length > 0 && (
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="text-purple-600" size={20} />
              Decisions & Recommendations
            </h3>
            <div className="space-y-2">
              {decisions.slice(0, 10).map((insight) => (
                <div key={insight.id} className="bg-slate-50 rounded-md px-4 py-3 text-sm text-slate-700">
                  {insight.data.value}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trends */}
        {trends.length > 0 && (
          <div className="card p-6 md:col-span-2">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-pink-600" size={20} />
              Market Trends
            </h3>
            <div className="space-y-2">
              {trends.slice(0, 10).map((insight) => (
                <div key={insight.id} className="bg-slate-50 rounded-md px-4 py-3 text-sm text-slate-700">
                  {insight.data.value}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
